import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Checkout, Depot, User, UserGroup } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import { historyBack } from '../../utils'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { AVAILABLE_LANGUAGES } from '../../configs/languages'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { UserGroupService } from '../../services/entity-services/userGroup.service'
import { CheckoutService } from '../../services/entity-services/checkout.service'
import { DepotService } from '../../services/entity-services/depot.service'
import { UserService } from '../../services/entity-services/user.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

export const userSchema = {
  id: null,
  locale: null,
  email: null,
  firstName: null,
  lastName: null,
  name: null,
  phone: null,
  enabled: null,
  groups: {
    id: null,
    name: null,
    roles: null,
  },
  roles: null,
  pin: null,
  company: {
    id: null,
    name: null,
    carrierRelations: {
      id: null,
    },
  },
  customers: {
    id: null,
    email: null,
    firstName: null,
    lastName: null,
    name: null,
    phone: null,
    note: null,
    wholesale: null,
  },
  checkouts: {
    id: null,
    name: null,
    enabled: null,
  },
  depots: {
    id: null,
    name: null,
  },
}

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styles: [
    `
      .form-check-input.fix-checkbox-align {
        margin-top: 0.45rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserComponent implements CheckDataChanges, OnDestroy {
  private userService = inject(UserService)
  private userGroupService = inject(UserGroupService)
  private checkoutService = inject(CheckoutService)
  private depotService = inject(DepotService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  loggedInUser = computed(() => this.core.services.user.user)
  visibleGroupes: string[] = []

  submitted = signal(false)
  private initialFormValue = signal<any>(null)
  isUserProfile = signal(false)

  private resolveUserId(): number | null {
    let id
    if (this.router.url.split('/')[1] === 'my' && this.router.url.split('/')[2] === 'profile') {
      id = this.core.services.user.user.id
    } else {
      id = Number(this.router.url.split('/')[2])
    }
    return id || null
  }

  isAdminGroupSelected(): boolean {
    return !!this.userForm.value.groups?.some((g: UserGroup) => g.name === 'Admin')
  }

  isNonAdminGroupSelected(): boolean {
    return !!this.userForm.value.groups?.some((g: UserGroup) => g.name !== 'Admin')
  }

  userForm = new FormGroup({
    email: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    plainPassword: new FormControl({ value: '', disabled: false }, []),
    firstName: new FormControl({ value: '', disabled: false }, [Validators.required]),
    lastName: new FormControl({ value: '', disabled: false }, [Validators.required]),
    phone: new FormControl({ value: '', disabled: false }, []),
    enabled: new FormControl({ value: false, disabled: false }, []),
    groups: new FormControl({ value: [], disabled: false }, []),
    roles: new FormControl({ value: [], disabled: false }, []),
    pin: new FormControl({ value: '', disabled: false }, []),
    checkouts: new FormControl({ value: [], disabled: false }, []),
    depots: new FormControl({ value: [], disabled: false }, []),
    locale: new FormControl({ value: '', disabled: false }, []),
  })

  public readonly availableLanguages = AVAILABLE_LANGUAGES

  readonly queryUserGroupsOptions = injectQuery(() => ({
    queryKey: ['userGroups', 'options'],
    queryFn: async () => {
      return await this.userGroupService
        .getUserGroups(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null, roles: null }
        )
        .then(result => {
          // todo const a = result.items.forEach(group => {
          //   this.groups.push(new UserGroup(group))
          // })
          return result.items
        })
    },
  }))

  readonly queryCheckouts = injectQuery(() => ({
    queryKey: ['checkouts'],
    queryFn: async () => {
      return await this.checkoutService
        .getCheckouts({
          page: 1,
          sort: 'id',
          direction: 'asc',
        })
        .then(result => {
          // return result.items.map(item => item.id)
          return result.items
        })
    },
  }))
  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      // If salesman with no assigned depots, return empty (existing business rule)
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        return []
      }
      // For user management, always offer full depot list so new depots are assignable immediately
      const result = await this.depotService.getDepots(
        {
          sort: 'name',
          direction: 'asc',
        },
        { id: null, name: null, enabled: null }
      )
      return result.items
    },
  }))

  readonly queryUser = injectQuery(() => ({
    // Keyed on the resolved id (not the raw route param) so the "create new" route
    // ('user', no id param -> resolves to null) and "my profile" route ('my/:profile',
    // whose param is named 'profile' not 'id' -> resolves to the logged-in user's id)
    // don't collide on the same cache entry and leak each other's form data.
    queryKey: ['user', this.resolveUserId()],
    queryFn: async () => {
      const id = this.resolveUserId()
      if (id) {
        return await this.userService.getUser(id, userSchema)
      } else {
        return new User()
      }
    },
  }))

  createUser = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.userForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()

      return this.userService.createUser(n as unknown as User, userSchema as any).then(result => {
        if (
          !result ||
          result?.error === true ||
          (Array.isArray(result?.errors) && result?.errors.length) ||
          !result.id
        ) {
          const msg = result?.message || result?.errors?.[0]?.message || 'Create user failed'
          const err = new Error(msg)
          ;(err as any).cause = result
          throw err
        }
        return result
        // return result
      }) // todo add schema
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.userForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.userForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.user.created'))

      // Update the detail cache for this user so consumers see fresh data immediately
      this.queryClient.setQueryData(['user', Number(data['id'])], data)

      // OPTIONS: keep as-is → update options cache so selects/filters see the new user immediately
      updateCachedListQueries(this.queryClient, ['users', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      // LISTING ['users', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeUserListings()

      // Optionally keep invalidation for full listing pages
      this.router.navigate(['/user', data['id']], { replaceUrl: true })
    },
    onError: error => {
      // An error happened!
      this.submitted.set(false)
    },
  }))

  updateUser = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.userForm.invalid) {
        throw new Error('Form is invalid')
      }
      const userDataToSave = this.getDataToSave()
      return this.userService.updateUser(userDataToSave as unknown as User, userSchema as any)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.userForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.userForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.user.updated'))

      // If the edited user is the currently logged-in user, apply the (possibly changed) locale
      // immediately - core.services.user.user is not refreshed by the update call itself, and
      // without this the UI stays on the old language until a full page reload.
      if (this.core.services.user.user?.id === data['id'] && data['locale']) {
        this.core.services.user.user.locale = data['locale']
        this.translateService.use(data['locale'])
      }

      // Update the detail cache for this user with returned server data
      this.queryClient.setQueryData(['user', data['id']], data)

      // OPTIONS: keep as-is → update options cache value for this user
      updateCachedListQueries(this.queryClient, ['users', 'options'], data, { mode: 'update' })

      // LISTING ['users', sortProp, sortDir]: On UPDATE → if id is present in cached pages, refetch that exact query; otherwise do nothing
      this.removeUserListings()
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id'] && !this.route.snapshot.params['profile']
  })
  loading = computed(() => {
    const primaryRequestPending =
      this.queryUser.isFetching() ||
      this.queryUser.isPending() ||
      this.createUser.isPending() ||
      this.updateUser.isPending()

    return (
      primaryRequestPending ||
      this.queryDepotsOptions.isFetching() ||
      this.queryDepotsOptions.isPending() ||
      this.queryCheckouts.isFetching() ||
      this.queryCheckouts.isPending() ||
      this.queryUserGroupsOptions.isFetching() ||
      this.queryUserGroupsOptions.isPending()
    )
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    effect(() => {
      const uG = this.queryUserGroupsOptions.data()
      const data = this.queryUser.data()
      if (data) {
        // Existing users change their password through the dedicated modal.
        // Keeping this hidden edit-form control required makes native form
        // validation block ngSubmit without any visible explanation.
        if (data['id']) {
          this.userForm.controls.plainPassword.clearValidators()
        } else {
          this.userForm.controls.plainPassword.setValidators([Validators.required])
        }
        this.userForm.controls.plainPassword.updateValueAndValidity()
        this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)

        const formValue = {
          name: data.name,
          email: data.email,
          plainPassword: data.plainPassword,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          enabled: data.enabled,
          groups: data.groups,
          roles: data.roles,
          pin: data.pin,
          checkouts: data.checkouts,
          depots: data.depots,
          locale: data.locale,
        }

        this.userForm.patchValue(formValue, { emitEvent: false })
        this.initialFormValue.set(structuredClone(formValue))
        this.userForm.markAsPristine()
      }
    })
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different user ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  getDataToSave() {
    const current = this.userForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    // const routeId = this.route.snapshot.params['id']
    const routeId = this.queryUser.data()?.id
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.email = current.email
    patch.firstName = current.firstName
    patch.lastName = current.lastName

    // plainPassword should be sent only if user filled it (cannot compare to initial)
    if ((current.plainPassword ?? '').toString().length > 0) {
      patch.plainPassword = current.plainPassword
    }

    // Optional fields – include only when changed against initial snapshot
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const idArray = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((x: any) => x?.id).filter((v: any) => v !== undefined && v !== null) : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }
    const eqArrayStrings = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.map(String).slice().sort() : []
      const bb = Array.isArray(b) ? b.map(String).slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    if (norm(current.phone) !== norm(initial?.phone)) {
      patch.phone = current.phone
    }
    if (norm(current.enabled) !== norm(initial?.enabled)) {
      patch.enabled = current.enabled
    }
    if (norm(current.pin) !== norm(initial?.pin)) {
      patch.pin = current.pin
    }
    if (norm(current.locale) !== norm(initial?.locale)) {
      patch.locale = current.locale
    }

    // Arrays: include only when changed
    if (!eqArrayIds(current.groups, initial?.groups)) {
      patch.groups = current.groups || []
    }
    if (!eqArrayStrings(current.roles, initial?.roles)) {
      patch.roles = current.roles || []
    }
    if (!eqArrayIds(current.checkouts, initial?.checkouts)) {
      patch.checkouts = current.checkouts || []
    }
    if (!eqArrayIds(current.depots, initial?.depots)) {
      patch.depots = current.depots || []
    }

    return patch
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.userForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // If initial is not yet set (e.g., brand-new form), fall back to form dirty flag
    if (!initial) {
      return this.userForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))
    const idArray = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((x: any) => x?.id).filter((v: any) => v !== undefined && v !== null) : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }
    const eqArrayStrings = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.map(String).slice().sort() : []
      const bb = Array.isArray(b) ? b.map(String).slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    const changed =
      norm(String(current?.email)) !== norm(String(initial?.email)) ||
      norm(String(current?.firstName)) !== norm(String(initial?.firstName)) ||
      norm(String(current?.lastName)) !== norm(String(initial?.lastName)) ||
      norm(String(current?.phone)) !== norm(String(initial?.phone)) ||
      norm(!!current?.enabled) !== norm(!!initial?.enabled) ||
      norm(String(current?.pin)) !== norm(String(initial?.pin))
    norm(String(current?.locale)) !== norm(String(initial?.locale)) ||
      norm(String(current?.plainPassword)) !== norm(String(initial?.plainPassword)) ||
      !eqArrayIds(current?.groups, initial?.groups) ||
      !eqArrayStrings(current?.roles, initial?.roles) ||
      !eqArrayIds(current?.checkouts, initial?.checkouts) ||
      !eqArrayIds(current?.depots, initial?.depots)
    return changed
  }

  onSubmit(): void {
    this.submitted.set(true)
    if (this.userForm.invalid) {
      return
    }
    const hasCheckoutOrDepot =
      (this.userForm.value.checkouts?.length ?? 0) > 0 || (this.userForm.value.depots?.length ?? 0) > 0
    if (hasCheckoutOrDepot && !confirm(this.translateService.instant('user.checkout-depot.save.confirmation'))) {
      this.submitted.set(false)
      return
    }
    if (this.isCreating()) {
      this.createUser.mutate()
    } else {
      this.updateUser.mutate()
    }
  }

  goBack(): void {
    historyBack()
  }

  updateGroup(group: UserGroup, value: boolean) {
    // Builds a new array instead of mutating userForm.value.groups in place: that array is the
    // same reference held by the query cache entry backing this form (see queryUser), so an
    // in-place push/splice here would silently corrupt cached data for other users/pages that
    // share the same reference (e.g. every future "create new user" visit within the cache's
    // staleTime, since new User() is otherwise a fresh, uncached-by-reference empty array).
    const groups = (this.userForm.value.groups ?? []) as UserGroup[]
    const next = value ? [...groups, group] : groups.filter((g: UserGroup) => g.id !== group.id)
    this.userForm.controls.groups.setValue(next)
  }

  updateCheckout(checkout: Checkout, value: boolean) {
    const checkouts = (this.userForm.value.checkouts ?? []) as Checkout[]
    const next = value ? [...checkouts, checkout] : checkouts.filter((c: Checkout) => c.id !== checkout.id)
    this.userForm.controls.checkouts.setValue(next)
  }

  updateDepot(depot: Depot, value: boolean) {
    const depots = (this.userForm.value.depots ?? []) as Depot[]
    const next = value ? [...depots, depot] : depots.filter((d: Depot) => d.id !== depot.id)
    this.userForm.controls.depots.setValue(next)
  }

  isUserInGroup(user: User | Partial<User>, group: UserGroup) {
    let result = false
    if (user?.groups?.length > 0) {
      user.groups.forEach((g: UserGroup | Partial<UserGroup>) => {
        if (g.id === group.id) {
          result = true
        }
      })
    }
    return result
  }

  hasUserCheckout(user: User | Partial<User>, checkout: Checkout) {
    let result = false
    if (user?.checkouts?.length > 0) {
      user.checkouts.forEach((c: Checkout | Partial<Checkout>) => {
        if (c.id === checkout.id) {
          result = true
        }
      })
    }
    return result
  }

  hasUserDepot(user: User | Partial<User>, depot: Depot) {
    let result = false
    if (user?.depots?.length > 0) {
      user.depots.forEach((d: Depot | Partial<Depot>) => {
        if (d.id === depot.id) {
          result = true
        }
      })
    }
    return result
  }

  isUserPermitted(user?: User) {
    let result = false
    const u = user ? user : this.userForm.value
    u.groups.forEach((g: UserGroup) => {
      g.roles?.forEach((r: string) => {
        if (r === 'ROLE_ADMIN' || r === 'ROLE_SUPER_ADMIN') {
          result = true
        }
      })
    })
    return result
  }

  rolesVisibilityChange(group) {
    if (this.areGroupRolesVisible(group))
      this.visibleGroupes = this.visibleGroupes.filter(groupName => groupName !== group.name)
    else this.visibleGroupes.push(group.name)
  }

  areGroupRolesVisible(group) {
    return this.visibleGroupes.some(groupName => groupName === group.name)
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.userForm.controls
  }

  generatePassword() {
    const newGeneratedPassword = this.randomPassword()
    this.userForm.controls.plainPassword.setValue(newGeneratedPassword)
  }

  randomPassword() {
    return Math.random().toString(36).slice(2) + Math.random().toString(36).toUpperCase().slice(2)
  }

  private removeUserListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['users'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }
}
